// UptimeBars — one mark per day in a row, the way a status page shows 90 days
// of a service (TOM-930). Each day is a rounded bar in a tone, as tall as its
// `level`, or the bare track when there is no data. A day's tooltip opens on
// hover, on keyboard focus and on tap, and stays open on tap so its links can
// be followed. The strip is one tab stop; the arrow keys move between days.
//
// State is never carried by hue alone: Night draws every status in one red, so
// a day marked `quiet` (nothing to report) is drawn fainter there, and the
// tooltip always names the day's state in words.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { toneColor, type Tone } from './types';

export interface UptimeBarDay {
  /** Stable key, usually the date. */
  key: string;
  /** The mark's tone; null draws only the track (no data). */
  tone: Tone | null;
  /** Height as a fraction of the strip, 0–1 (default 1). 0 is a stub on the baseline. */
  level?: number;
  /** Nothing to report: drawn fainter in Night, where every status is one red. */
  quiet?: boolean;
  /** The day in words, the tooltip's first line and what a screen reader hears. */
  label: string;
  /** More for the tooltip: figures, links. */
  detail?: ReactNode;
}

export interface UptimeBarsProps {
  days: readonly UptimeBarDay[];
  /** What the strip shows ("Website and API, last 90 days"). */
  ariaLabel: string;
  /** Height of a full bar in px (default 32). */
  height?: number;
  className?: string;
}

/** The index the arrow and Home/End keys move to, or null to leave it. */
export function nextUptimeIndex(key: string, current: number | null, count: number): number | null {
  if (count === 0) return null;
  const at = current ?? count - 1;
  switch (key) {
    case 'ArrowLeft':
      return Math.max(0, at - 1);
    case 'ArrowRight':
      return Math.min(count - 1, at + 1);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

export function UptimeBars({ days, ariaLabel, height = 32, className }: UptimeBarsProps) {
  const [active, setActive] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tipId = useId();

  const close = useCallback(() => {
    setActive(null);
    setPinned(false);
  }, []);

  // A pinned tooltip closes on a tap or click anywhere else.
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current !== null && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [pinned, close]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    const next = nextUptimeIndex(e.key, active, days.length);
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  };

  const day = active === null ? undefined : days[active];
  // Keep the tooltip inside the strip at its two ends.
  const along = active === null ? 0.5 : (active + 0.5) / Math.max(1, days.length);
  const align = along < 0.2 ? 'start' : along > 0.8 ? 'end' : 'center';

  return (
    <div
      ref={wrapRef}
      className={className === undefined ? 'chart-uptime' : `chart-uptime ${className}`}
      onPointerLeave={() => {
        if (!pinned) setActive(null);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
      }}
    >
      <div
        className="chart-uptime-strip"
        role="group"
        tabIndex={0}
        aria-label={`${ariaLabel}. Use the arrow keys to read each day.`}
        aria-describedby={day !== undefined ? tipId : undefined}
        style={{ height } as CSSProperties}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (active === null) setActive(days.length - 1);
        }}
      >
        {days.map((d, i) => {
          const level = Math.min(1, Math.max(0, d.level ?? 1));
          const classes = ['chart-uptime-day'];
          if (d.tone === null) classes.push('chart-uptime-day--empty');
          if (d.quiet === true) classes.push('chart-uptime-day--quiet');
          if (i === active) classes.push('chart-uptime-day--active');
          return (
            <span
              key={d.key}
              className={classes.join(' ')}
              data-day={d.key}
              onPointerEnter={() => {
                if (!pinned) setActive(i);
              }}
              onClick={() => {
                if (pinned && active === i) close();
                else {
                  setActive(i);
                  setPinned(true);
                }
              }}
            >
              {d.tone !== null && (
                <span
                  className="chart-uptime-mark"
                  style={
                    {
                      height: level === 0 ? undefined : `${level * 100}%`,
                      '--chart-tone': toneColor(d.tone),
                    } as CSSProperties
                  }
                  data-stub={level === 0 ? '' : undefined}
                />
              )}
            </span>
          );
        })}
      </div>
      {/* Arrow keys change the day; the reader hears it here. */}
      <span className="sr-only" aria-live="polite">
        {day?.label ?? ''}
      </span>
      {day !== undefined && (
        <div
          id={tipId}
          className={`chart-uptime-tip chart-uptime-tip--${align}`}
          role="tooltip"
          style={{ '--chart-uptime-at': `${along * 100}%` } as CSSProperties}
        >
          <div className="chart-uptime-tip-label">{day.label}</div>
          {day.detail !== undefined && <div className="chart-uptime-tip-detail">{day.detail}</div>}
        </div>
      )}
    </div>
  );
}
